import { useState } from 'react'
import { MAX_TABLE_SIZE } from 'utils'

import Button from 'components/uiKit/Button'
import InputNumber from 'components/uiKit/InputNumber'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { DEFAULT_BOTTOM_INDENT } from 'components/uiKit/Typography/constants'
import { t } from 'services/Translation'

import ColumnsControls from '../ColumnsControls'
import { DEFAULT_PAGE_SIZE } from '../constants'
import { useColumns, usePageSize } from '../table.hooks'
import type { ColumnState, ColumnType, ITablesExtendsData } from '../types'
import { getColumnsDefaultState } from '../utils'
import * as s from './TableOptions.module.scss'

export interface ITableOptionsFormProps<Data, Args extends Record<string, unknown>> {
  tableName: string
  defaultColumns?: ColumnType<Data>[]
  onSubmit?: () => void
  args?: Args
}

const exceptionalSymbolsOfTypeNumber = ['e', 'E', '+', '-', '.', ',', ' ']

const TableOptionsForm = <Data extends ITablesExtendsData>({
  tableName,
  defaultColumns,
  onSubmit,
}: ITableOptionsFormProps<Data, Record<string, unknown>>) => {
  const {
    tableColumnsState,
    setTableColumnsState,
    resetState: resetColumnsState,
  } = useColumns(tableName, defaultColumns)
  const [columnStatesDraft, setColumnStatesDraft] = useState<ColumnState[]>(tableColumnsState)
  const { pageSize, setPageSize } = usePageSize(tableName)
  const [paginationValue, setPaginationValue] = useState<number | null>(pageSize)

  const onApply = () => {
    setTableColumnsState(columnStatesDraft)
    setPageSize?.(paginationValue || DEFAULT_PAGE_SIZE)
    onSubmit?.()
  }

  const onReset = () => {
    if (defaultColumns) {
      resetColumnsState()
      setColumnStatesDraft(getColumnsDefaultState(defaultColumns))
    }

    setPageSize?.(DEFAULT_PAGE_SIZE)
    setPaginationValue(DEFAULT_PAGE_SIZE)
    onSubmit?.()
  }

  const onUserInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (exceptionalSymbolsOfTypeNumber.includes(event.key)) {
      event.preventDefault()
    }
  }
  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onApply()
    }
  }

  return (
    <div>
      {defaultColumns && (
        <ColumnsControls
          columnStates={columnStatesDraft}
          defaultColumns={defaultColumns}
          onChange={setColumnStatesDraft}
        />
      )}
      <Typography indents={DEFAULT_BOTTOM_INDENT} styleType='hint'>
        {t('table.pagination.maxSize', { max: MAX_TABLE_SIZE })}
      </Typography>
      <InputNumber
        defaultValue={pageSize}
        max={MAX_TABLE_SIZE}
        min={1}
        name='paginationInput'
        onChange={setPaginationValue}
        onKeyDown={onUserInput}
        onKeyPress={onKeyPress}
        placeholder={t('input.placeholder.enterValue')}
        size={KitSize.S}
        value={paginationValue}
      />
      <div className={s.modalContainerBtnsBlock}>
        <span>
          <Button
            name='cancel-save-pagination-value'
            onClick={() => onReset()}
            size={KitSize.S}
            styleType='ghost'
          >
            {t('common.restore')}
          </Button>
        </span>
        <span className={s.modalContainerBtnsBlockApplyBtn}>
          <Button name='save-pagination-btn' onClick={() => onApply()} size={KitSize.S}>
            {t('uiKit.button.apply')}
          </Button>
        </span>
      </div>
    </div>
  )
}

export default TableOptionsForm
